/**
 * Customer-facing wording. Errors never name the booking vendor: API-sourced text is scrubbed
 * before display and our own fallbacks stay generic.
 */
const VENDOR_NAMES = /\b(automaid|launch\s?27|l27)\b/gi;
/** Hostnames and Ruby class names leak the vendor too; remove them rather than word-substituting. */
const VENDOR_HOSTS = /\b[a-z0-9-]+\.(launch27|l27)\.(com|co)\b/gi;
const VENDOR_CLASSES = /\bTenant::\w+/g;

export const GENERIC = {
  busy: 'Our booking system is busy right now. Please wait a moment and try again.',
  offline: 'We could not reach our booking system. Check your connection and try again.',
  failed: 'Something went wrong while creating your booking. Please try again.',
  load: 'The booking form could not be loaded right now. Please refresh the page or try again later.',
  outOfArea: "We can't book this address online yet. Please get in touch and we'll see what we can do.",
  card: 'Your card could not be verified. Please check the details or try another card.',
  captcha: 'Please complete the captcha.',
} as const;

/**
 * Customer portal copy. These are keyed off HTTP status rather than passed through
 * `customerMessage`, deliberately: the vendor's own auth strings ("Account is locked. Try again
 * later.", "OTP token is required") are in its voice, and the lockout string is a weak oracle
 * telling an attacker they have tripped the limit. The cancel 404 is `text/plain`, which
 * `parseErrorEnvelope` passes through verbatim, so that path needs fixed copy too.
 */
export const PORTAL = {
  signInFailed: "That email and password don't match an account.",
  signInLocked: 'Too many sign-in attempts. Please wait a few minutes and try again.',
  signInThrottled: 'Please wait a moment before trying again.',
  signInOtp: 'Enter the 6-digit code from your authenticator app.',
  signInOtpInvalid: 'That code was not accepted. Check your authenticator app and try again.',
  sessionExpired: 'Your session has ended. Please sign in again.',
  signedOut: "You've been signed out.",
  resetSent: "If that email is on file, you'll receive a reset link shortly.",
  bookingsLoad: "We couldn't load your bookings right now. Please refresh and try again.",
  bookingLoad: "We couldn't load this booking right now. Please go back and try again.",
  cancelFailed: "We couldn't cancel this booking. Please call us and we'll sort it out.",
  cancelUnknown:
    "We couldn't confirm whether your cancellation went through. Here's the current status of your booking.",
  cancelNotAllowed: 'This booking can no longer be cancelled online. Please get in touch.',
  cancelBlocked: 'Please call us to cancel this booking and we will take care of it.',
  giftcardsLoad: "We couldn't load your gift cards right now. Please refresh and try again.",
  giftcardsEmpty: "You don't have any gift cards at the moment.",
  giftcardCopied: 'Code copied',
  cancelFeeUnknown:
    'There may be a cancellation fee for this booking. Please call us so we can confirm the amount.',
  rescheduleFailed: "We couldn't move this booking. Please call us and we'll find you a new time.",
  rescheduleUnknown:
    "We couldn't confirm whether your booking moved. Here's the current time we have for it.",
  rescheduleUnavailable:
    "This booking can't be moved online. Please get in touch and we'll rearrange it for you.",
  rescheduleDrift:
    'Your booking moved, but some other details may have changed too — please check them below.',

  /**
   * Shown where "Move this booking" would be, once the server stops permitting a move.
   * `{terms}` is replaced with a link whose text is `rescheduleLockedLink`; keeping the whole
   * sentence here means the wording and the link's position stay editable in one place.
   */
  rescheduleLocked:
    'In accordance with our reschedule policy this appointment is now locked-in and no further ' +
    'changes can be made. Thank you for your understanding and please feel free to read our full ' +
    '{terms} for more information.',
  rescheduleLockedLink: 'Terms & Conditions',

  /** Cancelling a whole recurring schedule, not one visit. */
  cancelSeriesNotice:
    'You are cancelling ALL recurring services. As we do book these appointments in advance and ' +
    'plan our schedule around them, we do require 30 days notice for a complete cancellation of ' +
    'recurring services.',
  cancelSeriesAll: 'Every future appointment in this schedule will be cancelled.',
  /**
   * `{fee}` is replaced with the formatted amount: the server's figure once it has named one,
   * otherwise ours from the policy. Ours can differ on a percentage policy (vendor question #13),
   * which is fine only because the customer must still tick the server's exact amount before
   * anything is charged — see CancelPanel.
   */
  cancelSeriesFee:
    'Please note there will be a cancellation fee of {fee} because you are cancelling your ' +
    'booking less than 30 days before your next appointment. The payment method associated ' +
    'with your account will be charged. If you wish to use a different payment method, please ' +
    'contact us at 405-458-4444.',
  /** Last resort: a fee applies but nothing lets us put a number on it. */
  cancelSeriesFeeUnknown:
    'Please note there will be a cancellation fee because you are cancelling your booking less ' +
    'than 30 days before your next appointment. The payment method associated with your account ' +
    'will be charged. If you wish to use a different payment method, please contact us at ' +
    '405-458-4444.',
} as const;

/** Removes vendor names from text that came from the API before a customer sees it. */
export function scrubVendorNames(text: string): string {
  // Hosts and class names first: substituting the bare name into a hostname would leave
  // "chasedetailing.our booking system.com" behind.
  return text
    .replace(VENDOR_HOSTS, 'our booking system')
    .replace(VENDOR_CLASSES, 'account')
    .replace(VENDOR_NAMES, 'our booking system')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Turns an error into something safe to show; `fallback` when there is nothing useful. */
export function customerMessage(err: unknown, fallback: string): string {
  const raw =
    typeof err === 'string'
      ? err
      : err && typeof err === 'object' && 'errorMessage' in err && typeof (err as { errorMessage: unknown }).errorMessage === 'string'
        ? (err as { errorMessage: string }).errorMessage
        : err instanceof Error
          ? err.message
          : '';
  const scrubbed = scrubVendorNames(raw);
  return scrubbed || fallback;
}

export function scrubFieldErrors<T extends Record<string, string[]>>(fieldErrors: T): T {
  const out: Record<string, string[]> = {};
  for (const [key, messages] of Object.entries(fieldErrors)) out[key] = messages.map(scrubVendorNames);
  return out as T;
}
